import { AlertTriangle, Pencil, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { area } from "@turf/turf";
import {
  useBoundaryFeatures,
  isGeometryTooBig,
  isTooManyFeatures,
  GEOMETRY_SIZE_LIMIT_KB,
} from "components/boundary";
import { zoomToFeature } from "pages/geoscape-data/explorer/utils";
import React, { useCallback, useState } from "react";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { BoundaryComponent } from "./boundary-component";
import { BoundaryItem } from "./boundary-item";
import { DrawComponent } from "./draw-component";
import { UploadComponent } from "./upload-component";

export type FeatureOrFeatureArray =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.Feature<GeoJSON.Geometry>[];

export interface BoundaryToolProps {
  selectedFeature: string | null;
  setSelectedFeature: React.Dispatch<React.SetStateAction<string | null>>;
  addOrUpdateFeatures: (feat: FeatureOrFeatureArray) => void;
  currentMode: string;
  setCurrentMode: React.Dispatch<React.SetStateAction<string>>;
}

const boundarySteps = [
  { stepName: "Upload", element: UploadComponent },
  { stepName: "Draw", element: DrawComponent },
  { stepName: "Boundary", element: BoundaryComponent },
];

const calculateTotalArea = (
  features: GeoJSON.Feature<GeoJSON.Geometry>[]
): number => {
  return features.reduce(
    (sum, feature) => sum + area(feature),
    0
  );
};

export const DefineBoundary = (
  props: ClipConfigProps & ClipStepsControlProps
) => {
  const [component, setComponent] = useState("Draw");

  const onGeometryChange = useCallback(
    (geometry: GeoJSON.FeatureCollection | undefined) => {
      props.setClipConfig((prev) => ({
        ...prev,
        geometry: geometry ?? { type: "FeatureCollection", features: [] },
      }));
    },
    [props.setClipConfig]
  );

  const {
    features,
    selectedFeatureId,
    currentMode,
    setCurrentMode,
    addOrUpdateFeatures,
    handleDelete,
    handleRename,
    handleSelect: baseHandleSelect,
  } = useBoundaryFeatures({
    draw: props.draw,
    mapRef: props.mapRef as React.RefObject<any>,
    initialGeometry: props.clipConfig.geometry,
    onGeometryChange,
  });

  // Extend select to also zoom to feature
  const handleSelectSidebar = useCallback(
    (id: string) => {
      baseHandleSelect(id);
      const selectedFeature = features.find((x) => x.id === id);
      if (selectedFeature) {
        zoomToFeature(props.mapRef, selectedFeature.geometry);
      }
    },
    [baseHandleSelect, features, props.mapRef]
  );

  const isConfigTooBig = isGeometryTooBig(features);
  const tooManyFeatures = isTooManyFeatures(features);
  const isAreaTooBig = calculateTotalArea(features) / 1000000 > 100;

  const typeCards = [
    { key: "Upload", icon: Upload, label: "Upload", sublabel: "ZIP(10MB)" },
    { key: "Draw", icon: Pencil, label: "Draw", sublabel: "Boundary" },
    { key: "Boundary", icon: Search, label: "Boundary", sublabel: "Search", disabled: true },
  ];

  return (
    <div className="flex flex-col gap-2 h-full">
      <h5 className="text-lg font-medium">Define Clip Boundary</h5>
      <div className="flex flex-row gap-2 justify-between">
        {typeCards.map((card) => {
          const Icon = card.icon;
          const isSelected = component === card.key;
          return (
            <button
              key={card.key}
              data-cy={card.key.toLowerCase()}
              disabled={card.disabled}
              onClick={() => !card.disabled && setComponent(card.key)}
              className={`w-[100px] h-[136px] flex flex-col justify-center items-center gap-1 rounded-lg border cursor-pointer transition-colors ${
                card.disabled
                  ? "opacity-40 pointer-events-none"
                  : isSelected
                  ? "bg-primary/10 border-primary"
                  : "hover:bg-muted"
              }`}
            >
              <div className="size-10 rounded-full bg-info/10 flex items-center justify-center">
                <Icon className="size-5 text-info" />
              </div>
              <span className="text-sm font-medium">{card.label}</span>
              <span className="text-xs text-muted-foreground">{card.sublabel}</span>
            </button>
          );
        })}
      </div>
      <div>
        {React.createElement(
          boundarySteps.filter((b) => b.stepName === component)[0].element,
          {
            selectedFeature: selectedFeatureId,
            setSelectedFeature: () => {},
            addOrUpdateFeatures,
            currentMode,
            setCurrentMode,
            ...props,
          }
        )}
      </div>

      <div className="flex flex-col gap-2 overflow-auto flex-1 min-h-0">
        <div className="flex flex-col gap-2">
          {features.map((feature, idx) => (
            <BoundaryItem
              key={feature.id as string}
              id={feature.id as string}
              selected={feature.id == selectedFeatureId}
              name={feature.properties?.name ?? `Boundaries ${idx}`}
              area={area(feature)}
              onDelete={handleDelete}
              onEdit={handleRename}
              onSelect={handleSelectSidebar}
            />
          ))}
        </div>
      </div>
      {isConfigTooBig && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertDescription>
            Geometry exceeds {GEOMETRY_SIZE_LIMIT_KB}kb, remove some features to
            continue
          </AlertDescription>
        </Alert>
      )}
      {tooManyFeatures && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertDescription>
            Number of features exceeds the maximum of 20, remove{" "}
            {features.length - 20} to continue
          </AlertDescription>
        </Alert>
      )}
      {isAreaTooBig && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertDescription>
            For large boundaries a commercial license could be more suitable.
          </AlertDescription>
        </Alert>
      )}
      <div className="self-end mt-auto flex">
        <Button
          disabled={
            features.length === 0 || isConfigTooBig || tooManyFeatures
          }
          onClick={() => {
            props.setActiveStep(
              Math.min(props.activeStep + 1, props.maxSteps)
            );
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
